import { useCallback, useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { PayeeCell } from "./shared/PayeeCell";
import { formatIst } from "./shared/formatIst";
import { firstName, groupByFirm } from "./shared/queueGrouping";
import { money } from "./PaymentListDetail";
import { Banknote, ChevronDown, ChevronRight, Download, AlertTriangle } from "lucide-react";

// Accounts-only queue of Director-Approved lines, grouped Payment List -> Firm.
// Paid lines stay visible (greyed). Lines are ticked per firm sub-group and marked
// Paid in bulk (status only — UTR/date are added later). Updates are applied in
// place — the screen is never refetched; failed lines revert individually.
export default function ExecutionQueueTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [downloading, setDownloading] = useState(null);

  const load = useCallback((pageNum = 1) => {
    setLoading(true);
    client
      .get("/payment-desk/execution-queue", { params: { page: pageNum, page_size: 20 } })
      .then((res) => {
        setGroups(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1); }, [load]);

  const patchLine = (lineId, patch) =>
    setGroups((gs) => gs.map((g) => ({ ...g, lines: g.lines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)) })));
  const setError = (id, msg) => setRowErrors((e) => ({ ...e, [id]: msg }));
  const clearError = (id) => setRowErrors((e) => { const { [id]: _drop, ...rest } = e; return rest; });

  const setPicked = (ids, on) =>
    setSelected((sel) => { const n = { ...sel }; ids.forEach((id) => { if (on) n[id] = true; else delete n[id]; }); return n; });

  const markPaid = async (lines) => {
    const ids = lines.map((l) => l.id);
    ids.forEach(clearError);
    setPicked(ids, false);
    lines.forEach((l) => patchLine(l.id, { status: "Paid" }));
    const results = await Promise.allSettled(
      lines.map((l) => client.post(`/payment-desk/execution-queue/lines/${l.id}/mark-paid`))
    );
    let failed = 0;
    results.forEach((r, i) => {
      const line = lines[i];
      if (r.status === "fulfilled") {
        patchLine(line.id, { ...r.value.data });
      } else {
        failed += 1;
        patchLine(line.id, { status: line.status });
        setError(line.id, `Line #${line.line_no}: ${getErrorMessage(r.reason)}`);
        setPicked([line.id], true);
      }
    });
    const ok = lines.length - failed;
    if (ok > 0) toast.success(`${ok} line${ok !== 1 ? "s" : ""} marked as Paid`);
    if (failed > 0) toast.error(`${failed} line${failed !== 1 ? "s" : ""} could not be marked Paid`);
  };

  const downloadBankList = async (listId, firm) => {
    const key = `${listId}-${firm.code}`;
    setDownloading(key);
    try {
      const res = await client.get(`/payment-desk/execution-queue/lists/${listId}/bank-list`, {
        params: { entity_code: firm.code },
        responseType: "blob",
      });
      let skipped = [];
      let exported = true;
      if ((res.data.type || "").includes("json")) {
        const body = JSON.parse(await res.data.text());
        skipped = body.skipped || [];
        exported = false;
      } else {
        const header = res.headers["x-skipped-lines"];
        skipped = header ? JSON.parse(decodeURIComponent(header)) : [];
        const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const stamp = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${listId}-${firm.name}-${stamp}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      setWarnings((w) => ({ ...w, [key]: skipped }));
      if (exported) toast.success("Bank list downloaded");
      else if (skipped.length === 0) toast.error("No Approved lines to export for this firm");
      else toast.error("No file produced — every line was skipped");
    } catch (err) {
      let msg = getErrorMessage(err);
      try {
        const body = JSON.parse(await err.response.data.text());
        msg = body.detail || msg;
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const colSpan = 7;

  return (
    <div className="space-y-3" data-testid="execution-queue-tab">
      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white py-8 text-center text-slate-400" data-testid="execution-queue-loading">Loading...</div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white py-8 text-center text-slate-400" data-testid="execution-queue-empty">No approved lines awaiting payment.</div>
      ) : (
        groups.map((g) => {
          const open = !!expanded[g.list_id];
          const total = g.lines.reduce((sum, l) => sum + l.amount, 0);
          const creators = [...new Set(g.lines.map((l) => l.created_by_name))];
          return (
            <div key={g.list_id} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`execution-group-${g.list_id}`}>
              <button
                type="button"
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-slate-50"
                onClick={() => setExpanded((e) => ({ ...e, [g.list_id]: !e[g.list_id] }))}
                data-testid={`execution-group-header-${g.list_id}`}
              >
                {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <span className="font-mono text-sm font-semibold">{g.list_id}</span>
                <span className="text-xs text-slate-500" data-testid={`execution-group-count-${g.list_id}`}>{g.lines.length} line{g.lines.length !== 1 ? "s" : ""}</span>
                <span className="text-sm font-medium" data-testid={`execution-group-total-${g.list_id}`}>{money(total)}</span>
                {creators.length === 1 && (
                  <span className="text-xs text-slate-500" data-testid={`execution-group-submitter-${g.list_id}`}>
                    {firstName(creators[0])} · {formatIst(g.first_submitted_at)}
                  </span>
                )}
              </button>

              {open && (
                <div className="border-t border-slate-200" data-testid={`execution-group-body-${g.list_id}`}>
                  <Table data-testid={`execution-lines-table-${g.list_id}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>Line #</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Paid To</TableHead>
                        <TableHead>Bill</TableHead>
                        <TableHead className="min-w-[160px]">Attributed To</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="w-[130px]">Approved By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupByFirm(g.lines).flatMap((firm) => {
                        const wKey = `${g.list_id}-${firm.code}`;
                        const warn = warnings[wKey];
                        const eligible = firm.items.filter((l) => l.status !== "Paid");
                        const picked = eligible.filter((l) => selected[l.id]);
                        const allPicked = eligible.length > 0 && picked.length === eligible.length;
                        const rows = [
                          <TableRow key={`firm-${firm.name}`} className="bg-slate-100/70 hover:bg-slate-100/70" data-testid={`execution-firm-header-${g.list_id}-${firm.name}`}>
                            <TableCell className="py-2">
                              <Checkbox
                                checked={allPicked}
                                disabled={eligible.length === 0}
                                onCheckedChange={(v) => setPicked(eligible.map((l) => l.id), v === true)}
                                data-testid={`select-all-${g.list_id}-${firm.name}`}
                              />
                            </TableCell>
                            <TableCell colSpan={colSpan} className="py-2">
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                <span data-testid={`execution-firm-name-${g.list_id}-${firm.name}`}>{firm.name}</span>
                                <span className="font-normal normal-case text-slate-500" data-testid={`execution-firm-count-${g.list_id}-${firm.name}`}>{firm.items.length} line{firm.items.length !== 1 ? "s" : ""}</span>
                                <span className="normal-case text-slate-700" data-testid={`execution-firm-subtotal-${g.list_id}-${firm.name}`}>{money(firm.total)}</span>
                                {firm.code && (
                                  <Button
                                    size="sm" variant="outline" className="h-7 normal-case"
                                    disabled={downloading === wKey}
                                    onClick={() => downloadBankList(g.list_id, firm)}
                                    data-testid={`download-bank-list-${g.list_id}-${firm.code}`}
                                  >
                                    <Download className="h-3.5 w-3.5 mr-1" /> {downloading === wKey ? "Preparing..." : "Download bank list"}
                                  </Button>
                                )}
                              </div>
                              {warn && warn.length > 0 && (
                                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs normal-case font-normal text-amber-900" data-testid={`bank-list-warning-${g.list_id}-${firm.code}`}>
                                  <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {warn.length} line{warn.length !== 1 ? "s" : ""} left out of the bank list:</p>
                                  <ul className="mt-1 list-disc pl-5">
                                    {warn.map((w) => <li key={w.line_no}>Line #{w.line_no} — {w.reason}</li>)}
                                  </ul>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>,
                        ];
                        if (picked.length > 0) {
                          rows.push(
                            <TableRow key={`bar-${firm.name}`} className="bg-amber-50 hover:bg-amber-50" data-testid={`execution-bulk-bar-${g.list_id}-${firm.name}`}>
                              <TableCell colSpan={colSpan + 1} className="py-2">
                                <div className="flex items-center justify-end gap-3">
                                  <span className="text-xs text-slate-600" data-testid={`execution-selected-count-${g.list_id}-${firm.name}`}>{picked.length} selected</span>
                                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => markPaid(picked)} data-testid={`mark-paid-selected-${g.list_id}-${firm.name}`}>
                                    <Banknote className="h-3.5 w-3.5 mr-1" /> Mark Paid ({picked.length})
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        firm.items.forEach((line) => {
                          const paid = line.status === "Paid";
                          const creatorRemark = (line.purpose || "").trim();
                          const err = rowErrors[line.id];
                          const hasNote = creatorRemark || err;
                          rows.push(
                            <TableRow key={line.id} data-testid={`execution-line-row-${line.id}`} className={`${hasNote ? "border-b-0" : ""} ${paid ? "bg-slate-50 text-slate-400" : ""}`}>
                              <TableCell>
                                {!paid && (
                                  <Checkbox checked={!!selected[line.id]} onCheckedChange={(v) => setPicked([line.id], v === true)} data-testid={`select-line-${line.id}`} />
                                )}
                              </TableCell>
                              <TableCell className="font-mono text-xs">#{line.line_no}</TableCell>
                              <TableCell className="text-xs">{line.vendor_name || "—"}</TableCell>
                              <TableCell><PayeeCell line={line} /></TableCell>
                              <TableCell className="font-mono text-xs">{line.vendor_bill_id || "—"}</TableCell>
                              <TableCell className="text-xs">{line.site_label || line.equipment_label}</TableCell>
                              <TableCell>{money(line.amount)}</TableCell>
                              <TableCell className="text-xs whitespace-nowrap">
                                {firstName(line.approved_by_name)}
                                <span className="block text-slate-400">{line.approved_at ? new Date(line.approved_at).toLocaleString("en-IN") : ""}</span>
                              </TableCell>
                            </TableRow>
                          );
                          if (hasNote) {
                            rows.push(
                              <TableRow key={`${line.id}-note`} data-testid={`execution-line-note-${line.id}`}>
                                <TableCell colSpan={colSpan + 1} className="pt-0 pb-2 space-y-0.5">
                                  {creatorRemark && <p className="text-xs italic text-slate-600" data-testid={`execution-line-creator-remark-${line.id}`}>Creator's remark: {creatorRemark}</p>}
                                  {err && <p className="text-xs text-red-600" data-testid={`execution-line-error-${line.id}`}>{err}</p>}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        });
                        return rows;
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          );
        })
      )}
      <PaginationBar page={page} totalPages={totalPages} onPageChange={(p) => { setRowErrors({}); load(p); }} testId="execution-queue-pagination" />

    </div>
  );
}
